import _ from 'lodash';
import { z } from 'zod';
import { FlowTestAssertionSchema } from './assertion.js';
import { FlowTestStartPayloadSchema } from './startPayload.js';
/** The expected terminal state is independent of assertions about individual blocks. */
export const FlowTestExpectedRunStateSchema = z.enum(['Completed', 'Failed']);
export const FlowTestExpectedRunState = FlowTestExpectedRunStateSchema.enum;
/** Block IDs keep mocks stable across non-unique names and renames. */
export const FlowTestBlockMocksByIdSchema = z.record(z.string(), z.unknown());
const BlockConfigMocksByIdSchema = z.record(z.string(), z.record(z.string(), z.unknown()));
/** Unsafe blocks that a builder explicitly allows the case to run for real. */
export const FlowTestLiveBlockIdsSchema = z.array(z.string()).default([]);
/** The storage-independent contract generated and accepted for one runnable test case. */
export const RunnableFlowTestCaseSchema = z.object({
    name: z.string().min(1, 'A test case needs a name'),
    description: z.string().optional(),
    startPayload: FlowTestStartPayloadSchema,
    expectedRunState: FlowTestExpectedRunStateSchema.default(FlowTestExpectedRunState.Completed),
    blockOutputMocks: FlowTestBlockMocksByIdSchema.default({}),
    blockConfigMocks: BlockConfigMocksByIdSchema.default({}),
    liveBlockIds: FlowTestLiveBlockIdsSchema,
    assertions: z
        .array(FlowTestAssertionSchema)
        .min(1, 'A test case needs at least one assertion')
        .refine((assertions) => _.uniqWith(assertions, (first, second) => first.blockId === second.blockId && first.name === second.name).length === assertions.length, 'Assertions on the same block need different names'),
});
/** Removes defaults before making fields optional so an omitted patch field stays omitted. */
export const PartialRunnableFlowTestCaseSchema = RunnableFlowTestCaseSchema.extend({
    expectedRunState: FlowTestExpectedRunStateSchema,
    blockOutputMocks: FlowTestBlockMocksByIdSchema,
    blockConfigMocks: BlockConfigMocksByIdSchema,
    liveBlockIds: z.array(z.string()),
}).partial();
/** Path parameters the legacy test case endpoints accept. */
export const FlowTestCaseIdParamsSchema = z.object({
    id: z.uuid().describe('The test case.'),
});
/**
 * Body `POST /test-cases` accepts.
 *
 * This is the older test case, saved against a flow definition with its mocks and its assertions
 * in one request. Its assertions are a simpler thing than `FlowTestAssertionSchema`: a comparator
 * that only tests equality, keyed by the block it applies to.
 */
export const FlowTestCaseCreateBodySchema = z.object({
    name: z.string(),
    entityId: z.uuid().describe('The entity the case runs against.'),
    entityType: z.string(),
    practiceId: z.uuid(),
    flowDefinitionId: z.uuid().describe('The definition under test.'),
    blockOutputMocks: z
        .record(z.string(), z.unknown())
        .describe('What each mocked block returns, by block id.')
        .optional(),
    assertions: z
        .record(z.string(), z.array(z.object({
        name: z.string(),
        comparatorKind: z.enum(['=']),
        comparatorValue: z.unknown().optional(),
        // Whole-output equality is all the legacy executor judges; anything else it stores and
        // then fails forever.
        comparatorTarget: z.literal('output'),
    })))
        .describe('What to check once the run finishes, by block id.')
        .optional(),
});
//# sourceMappingURL=testCase.js.map